import stationarySourceService from "@/views/stationarySource/stationarySourceService";
import { apiService, type PagingApiAndNotifierConfig, type Paging } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import { ToastOptionsSuccess, ToastOptionsError } from "@modernary/mui/components/toasts";
import { apiHosts } from "@/api/constants/apiHosts";

import type {
  StationarySource,
  StationarySourceNew,
  StationarySourceQuery,
  CanDeleteStationarySource,
} from "./stationarySource";
import type { AxiosResponse } from "axios";
import { LE } from "@/constants/enums";

const URL = "stationary-sources";
const ENTITY_NAME = "Stationary Source";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_AQMS_API_BASE_URL,
};
const api = apiService<StationarySource>(apiHosts.AQMS, axiosParams);

const getStationarySources = (params: StationarySourceQuery, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successToastCb,
    errorToastCb,
  });
};

const getStationarySourceById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const getNewStationarySource = async (ids: StationarySourceIds) => {
  const defaultApp = {
    data: {
      ...stationarySourceService.getInitialData(),
      ...ids,
      stateMurn: LE.State.NV,
    },
  } as AxiosResponse<StationarySourceNew, any>;

  return defaultApp;
};

type StationarySourceIds = {
  id?: string | number;
};
const getStationarySourceByIdOrNew = (ids: StationarySourceIds, config: PagingApiAndNotifierConfig = {}) => {
  if (!ids.id) {
    return getNewStationarySource(ids);
  } else {
    return getStationarySourceById(ids.id, config) as Promise<AxiosResponse<StationarySourceNew, any>>;
  }
};

const createStationarySource = (stationarySource: StationarySource, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.post(URL, stationarySource, {
    successToastCb,
    errorToastCb,
  });
};

const updateStationarySource = (stationarySource: StationarySource, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.put(URL + "/" + stationarySource.id, stationarySource, {
    successToastCb,
    errorToastCb,
  });
};

/**
 * @param {StationarySource} stationarySource This cannot be a 'ref' to a stationarySource
 */
const saveStationarySource = (stationarySource: StationarySource, config = {}) => {
  if (stationarySource.id) {
    return updateStationarySource(stationarySource, config);
  } else {
    return createStationarySource(stationarySource, config);
  }
};

const deleteStationarySourceById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successToastCb,
    errorToastCb,
  });
};

const canDeleteApi = apiService<CanDeleteStationarySource, Paging<CanDeleteStationarySource>>(
  apiHosts.AQMS,
  axiosParams,
);
const canDeleteStationarySource = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successToastCb = (options: ToastOptionsSuccess) => {
    options.hide = true;
    config?.successToastCb?.(options);
  };
  const errorToastCb = (options: ToastOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorToastCb?.(options);
  };

  return canDeleteApi.get(URL + "/can-delete/" + id, {
    successToastCb,
    errorToastCb,
  });
};

export {
  deleteStationarySourceById,
  getStationarySources,
  getStationarySourceById,
  getNewStationarySource,
  getStationarySourceByIdOrNew,
  createStationarySource,
  updateStationarySource,
  saveStationarySource,
  canDeleteStationarySource,
};
